import { EnvironmentConfig } from "./config";

const SESSION_GATE_TIMEOUT_MS = 5000;
const MAIN_IMPORT_RETRY_STORAGE_KEY = "lmdocs.bootstrap.main-import-retries";
const MAIN_IMPORT_RETRY_LIMIT = 2;
const MAIN_IMPORT_RETRY_DELAY_MS = 1500;

type SessionGateResult =
  | { status: "valid"; user: any }
  | { status: "anonymous" }
  | { status: "unknown" };

declare global {
  interface Window {
    __LMDOCS_SESSION_PREFETCH__?: {
      valid: boolean;
      user: any;
      checked_at_ms: number;
      source: "index-gate";
    };
  }
}

function buildRedirectTarget(): string {
  try {
    return `${window.location.pathname}${window.location.search}`;
  } catch {
    return "/";
  }
}

function buildLoginRedirectUrl(reason: string): string {
  const redirect = encodeURIComponent(buildRedirectTarget());
  return `/login?reason=${encodeURIComponent(reason)}&redirect=${redirect}`;
}

function normalizeUserFromVerifyPayload(data: any): any {
  if (!data || typeof data !== "object") return null;

  const rawUser = (data as any).user;
  if (rawUser && typeof rawUser === "object") {
    const id = typeof rawUser.id === "string" ? rawUser.id.trim() : "";
    if (!id) return null;
    const scopes = Array.isArray(rawUser.scopes) ? rawUser.scopes : [];
    return { id, scopes };
  }

  const userId = typeof (data as any).user_id === "string" ? (data as any).user_id.trim() : "";
  if (!userId) return null;
  const scopes = Array.isArray((data as any).scopes) ? (data as any).scopes : [];
  return { id: userId, scopes };
}

async function verifySessionGate(): Promise<SessionGateResult> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), SESSION_GATE_TIMEOUT_MS);

  try {
    await EnvironmentConfig.init();
    const apiBaseUrl = EnvironmentConfig.getCachedApiBaseUrl();
    const normalizedBase = apiBaseUrl && apiBaseUrl !== window.location.origin ? apiBaseUrl.replace(/\/+$/, "") : "";
    const verifyUrl = `${normalizedBase}/api/auth/verify`;

    const response = await fetch(verifyUrl, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
      cache: "no-store",
      signal: controller.signal,
    });

    if (response.status === 401 || response.status === 403) {
      return { status: "anonymous" };
    }

    if (!response.ok) {
      return { status: "unknown" };
    }

    const data = await response.json().catch(() => null);
    const okFlag = Boolean((data as any)?.valid ?? (data as any)?.ok ?? true);
    const user = normalizeUserFromVerifyPayload(data);

    if (okFlag && user) {
      return { status: "valid", user };
    }

    return { status: "unknown" };
  } catch {
    return { status: "unknown" };
  } finally {
    window.clearTimeout(timeout);
  }
}

function readMainImportRetryCount(): number | null {
  try {
    const stored = Number.parseInt(window.sessionStorage.getItem(MAIN_IMPORT_RETRY_STORAGE_KEY) || "0", 10);
    return Number.isFinite(stored) && stored >= 0 ? stored : 0;
  } catch {
    return null;
  }
}

function clearMainImportRetryCount(): void {
  try {
    window.sessionStorage.removeItem(MAIN_IMPORT_RETRY_STORAGE_KEY);
  } catch {
    // Storage may be unavailable in hardened browser contexts.
  }
}

function writeMainImportRetryCount(retryCount: number): boolean {
  try {
    window.sessionStorage.setItem(MAIN_IMPORT_RETRY_STORAGE_KEY, String(retryCount));
    return true;
  } catch {
    return false;
  }
}

async function loadMainBundle(): Promise<void> {
  try {
    await import("./main");
    clearMainImportRetryCount();
  } catch (error) {
    const retryCount = readMainImportRetryCount();
    if (retryCount === null || retryCount >= MAIN_IMPORT_RETRY_LIMIT) {
      throw error;
    }

    if (!writeMainImportRetryCount(retryCount + 1)) {
      throw error;
    }
    await new Promise((resolve) => window.setTimeout(resolve, MAIN_IMPORT_RETRY_DELAY_MS));
    window.location.reload();
  }
}

async function bootstrapIndex(): Promise<void> {
  const session = await verifySessionGate();

  if (session.status === "anonymous") {
    window.location.replace(buildLoginRedirectUrl("expired"));
    return;
  }

  if (session.status === "valid") {
    window.__LMDOCS_SESSION_PREFETCH__ = {
      valid: true,
      user: session.user,
      checked_at_ms: Date.now(),
      source: "index-gate",
    };
  }

  await loadMainBundle();
}

void bootstrapIndex();
